/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  Home as HomeIcon,
} from 'react-feather';
import {
  PostAdd as PostAddIcon,
  Web as WebIcon,
  ChromeReaderMode as ChromeReaderModeIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  
} from '@material-ui/icons';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';

const sections = [
  {
    subheader: 'Reports',
    items: [
      {
        title: 'Dashboard',
        icon: HomeIcon,
        href: '/app/reports/dashboard'
      },
    ]
  },
  {
    subheader: 'Traffic Sources',
    items: [
      {
        title: 'Campaigns',
        icon: WebIcon,
        href: '/app/ts/campaigns',
        items: [
          {
            title: 'List Campaigns',
            href: '/app/ts/campaigns'
          },
          {
            title: 'Create Campaign',
            href: '/app/ts/campaigns/create'
          },
          {
            title: 'Mass Widget Optimization',
            href: '/app/ts/campaigns/widgets/optimization/mass/create'
          },
          {
            title: 'Auto Widget Optimization',
            href: '/app/ts/campaigns/widgets-auto-optimization'
          },
          {
            title: 'Auto Campaign Optimization',
            href: '/app/ts/campaigns/campaigns-auto-optimization'
          },
        ]
      },
      {
        title: 'Content Ads',
        icon: ChromeReaderModeIcon,
        href: '/app/ts/contents',
        items: [
          {
            title: 'List Content Ads',
            href: '/app/ts/contents'
          },
          {
            title: 'Create Content Ad',
            href: '/app/ts/contents/create'
          },
        ]
      },
      {
        title: 'Requests',
        icon: PostAddIcon,
        href: '/app/ts/requests',
        items: [
          {
            title: 'Campaign Submissions',
            href: '/app/ts/requests/campaign/submissions'
          },
          {
            title: 'Content Submissions',
            href: '/app/ts/requests/content/submissions'
          },
        ]
      },
      {
        title: 'Expense Report',
        icon: DescriptionIcon,
        href: '/app/ts/expense-report',
        items: [
          {
            title: 'Campaigns',
            href: '/app/ts/expense-report/campaigns'
          },
          {
            title: 'Schedule',
            href: '/app/ts/expense-report/schedule'
          },
        ]
      },
    ]
  },
  {
    subheader: 'Thrive',
    items: [
      {
        title: 'Enhanced Optimization',
        icon: AssessmentIcon,
        href: '/app/thrive/eo',
        items: [
          {
            title: 'Auto Widget Optimization',
            href: '/app/thrive/eo/widgets-auto-optimization'
          },
          {
            title: 'Report',
            href: '/app/thrive/eo/report'
          },
        ]
      },
    ]
  }
];

function renderNavItems({
  items,
  pathname,
  depth = 0
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={2}
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/app/account">
              <Avatar
                alt={user.name}
                className={classes.avatar}
                src={user.avatar}
              />
            </RouterLink>
          </Box>
          <Box
            mt={2}
            textAlign="center"
          >
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user.name}
            </Link>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Your tier:
              {' '}
              <Link
                component={RouterLink}
                to="/app/account"
              >
                {user.tier}
              </Link>
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {section.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        <Box p={2}>
          <Box
            p={2}
            borderRadius="borderRadius"
            bgcolor="background.dark"
          >
            <Typography
              variant="h6"
              color="textPrimary"
            >
             Presented by:
            </Typography>
            <Link
              variant="subtitle1"
              color="secondary"
              href="https://bebetteradvertising.com/"
              target="_blank"
            >
              Be Better Advertising
            </Link>
          </Box>
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
